import React, { useState } from 'react';
import { ShieldAlert, Stethoscope } from 'lucide-react';

/**
 * ClinicalFlagConfirmModal — second-activation gate for any feature flag
 * carrying `requiresClinicalSignoff`. Enabling clinical content without
 * documented licensed-clinician sign-off + liability-insurance confirmation
 * is a Rule 8a violation, so turning such a flag ON requires a deliberate
 * type-to-confirm step. Turning it OFF is one-tap (fail-safe toward disabled)
 * and never reaches this modal.
 *
 * Self-contained: owns its typed-phrase state, resets on each open. Parent
 * only supplies the target flag + confirm/cancel handlers.
 */
const CONFIRM_PHRASE = 'ENABLE CLINICAL';

const ClinicalFlagConfirmModal = ({ flag, busy, onCancel, onConfirm }) => {
  const [typed, setTyped] = useState('');
  if (!flag) return null;

  const matches = typed.trim() === CONFIRM_PHRASE;

  const handleCancel = () => {
    setTyped('');
    onCancel();
  };
  const handleConfirm = () => {
    if (!matches || busy) return;
    setTyped('');
    onConfirm();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      onClick={() => !busy && handleCancel()}
      data-testid="clinical-flag-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-enf-surface-deep border border-enf-role-warning/40 rounded-xl p-6"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-enf-role-warning/15 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-enf-role-warning" />
          </div>
          <div className="flex-1">
            <div className="font-display text-2xl tracking-wide leading-none mb-1">Enable clinical content?</div>
            <div className="text-sm text-enf-text-secondary">
              You are about to turn on <span className="text-enf-text-primary font-semibold">{flag.label}</span> for
              every user.
            </div>
          </div>
        </div>

        <div className="bg-enf-role-warning/10 border border-enf-role-warning/30 rounded-md p-3 mb-4 text-xs text-enf-text-secondary leading-relaxed">
          <div className="flex items-center gap-1.5 text-enf-role-warning font-semibold uppercase tracking-widest mb-1.5">
            <Stethoscope className="w-3.5 h-3.5" /> Sign-off not documented
          </div>
          This flag exposes clinical content. It must NOT be enabled in production until{' '}
          <span className="text-enf-text-primary font-semibold">documented licensed-clinician sign-off</span> AND{' '}
          <span className="text-enf-text-primary font-semibold">liability-insurance confirmation</span> are on file
          (Rule 8a). Enabling it early puts unreviewed clinical guidance in front of real users.
        </div>

        <div className="bg-enf-surface-mid border border-enf-surface-border rounded-md p-3 mb-4">
          <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">
            Type <span className="font-mono text-enf-role-warning">{CONFIRM_PHRASE}</span> to confirm
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck="false"
            className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm font-mono focus:outline-none focus:border-enf-role-warning"
            data-testid="clinical-flag-confirm-input"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleCancel}
            disabled={busy}
            className="flex-1 px-4 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-medium transition-colors"
            data-testid="clinical-flag-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy || !matches}
            className="flex-1 px-4 py-2 bg-enf-role-warning hover:bg-enf-role-warning-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
            data-testid="clinical-flag-confirm"
          >
            <ShieldAlert className="w-4 h-4" /> {busy ? 'Enabling…' : 'Enable clinical content'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicalFlagConfirmModal;
